import type { ComponentProps, ReactNode } from 'react'
import {
  FieldInfo,
  hasFieldError,
  readOnlyTriggerClass,
  useFieldContext,
  useFormReadOnly,
  useIsFieldOptional,
} from './shared'
import { Combobox } from '../combobox'
import { FieldLabelRow } from './field-label-row'
import { cn } from '@/lib/utils'
import { HORIZONTAL_CONTROL_CLASS, HORIZONTAL_LABEL_CLASS, HORIZONTAL_ROW_CLASS } from './horizontal-field'

export default function ComboboxNumberField(
  props: ComponentProps<'select'> & {
    label: string
    placeholder?: string
    search: string
    empty: string
    options: { value: number; label: string }[]
    variant?: 'default' | 'outline'
    children?: ReactNode
    orientation?: 'vertical' | 'horizontal'
    optional?: boolean
  },
) {
  const field = useFieldContext<number | null>()
  const formReadOnly = useFormReadOnly()
  const isOptional = useIsFieldOptional(field.name, props.optional)
  const isHorizontal = props.orientation === 'horizontal'

  const stringOptions = props.options.map((option) => ({
    value: option.value.toString(),
    label: option.label,
  }))

  return (
    <div className={cn(props.className, formReadOnly && readOnlyTriggerClass)}>
      <div className={isHorizontal ? HORIZONTAL_ROW_CLASS : 'grid gap-2'}>
        {props.label && (
          <FieldLabelRow
            label={props.label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
            compactOptional={isHorizontal}
            className={isHorizontal ? HORIZONTAL_LABEL_CLASS : undefined}
          />
        )}
        <div className={isHorizontal ? HORIZONTAL_CONTROL_CLASS : undefined}>
          <Combobox
            id={field.name}
            placeholder={formReadOnly ? '-' : props.placeholder}
            commandEmpty={props.empty}
            searchPlaceholder={props.search}
            variant={props.variant ?? 'outline'}
            options={stringOptions}
            value={field.state.value?.toString() ?? ''}
            onChange={(val) => field.handleChange(val === '' ? null : Number(val))}
            isError={hasFieldError(field.state.meta)}
            children={props.children}
          />
          <FieldInfo fieldState={field.state} />
        </div>
      </div>
    </div>
  )
}
